import { useReducer, useEffect } from 'react'
import { Order, IPageInfo, IProjectionImport } from '@api/types'
import { uniqueId } from 'lodash'
import { useSelection } from '@hooks'
import { ISelection } from '@hooks/useSelection'
import { useShouldReload, useReload } from '@hooks/reloadContext'
import { Toaster } from '@ui'
import { ApolloError } from 'apollo-boost'
import api from '@api'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  SORT,
  NEXT_PAGE,
  QUERY,
  ERROR,
  CHANGE_TAB,
  DETAIL_VIEW,
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: string
}

interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: string
}

interface LoadAction {
  type: Actions.LOAD
  reqId: string
  projections: IProjectionImport[]
  count: number
  pageInfo: IPageInfo
}

interface SortAction {
  type: Actions.SORT
  column: string
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

interface QueryAction {
  type: Actions.QUERY
  query: string
}

interface ErrorAction {
  type: Actions.ERROR
  reqId: string
}
interface ChangeTabAction {
  type: Actions.CHANGE_TAB
  tab: string
}

interface DetailViewAction {
  type: Actions.DETAIL_VIEW
  detail: IProjectionImport | null
}

export interface IDHash {
  [id: string]: boolean
}

type Action =
  | LoadAction
  | LoadStartAction
  | NextPageAction
  | SortAction
  | QueryAction
  | ErrorAction
  | LoadStartActionWithoutSkeleton
  | ChangeTabAction
  | DetailViewAction

interface Sort {
  column: string
  order: Order
}

interface State {
  reqId: string
  isLoading: boolean
  isSorting: boolean
  isPaging: boolean
  count: number
  items: IProjectionImport[]
  after: string | null
  pageInfo: IPageInfo
  sort: Sort
  query: string
  first: number
  tab: string
  detail: IProjectionImport | null
}

export interface UseProjectionsHook extends State {
  sortBy: (column: string) => void
  nextPage: () => void
  applyQuery: (query: string) => void
  clearQuery: () => void
  reload: () => void
  selection: ISelection
  changeTab: (tab: string) => void
  selectDetail: (detail: IProjectionImport | null) => void
}

let persistedState: State = {
  reqId: uniqueId('request'),
  isLoading: true,
  isSorting: false,
  isPaging: false,
  count: 0,
  first: PAGE_SIZE,
  items: [],
  after: null,
  sort: { column: 'name', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  query: '',
  tab: 'building',
  detail: null,
}

interface GetVariablesFunc {
  query: string
  after?: string | null
  first?: number
}

export function getVariables({ after, first }: GetVariablesFunc) {
  return {
    after,
    first,
  }
}

export default function useProjections(): UseProjectionsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const selection = useSelection()
  const shouldReload = useShouldReload()
  const reload = useReload()

  const fetchProjections =
    ({ showSkeleton, loadAll }: { showSkeleton: boolean; loadAll: boolean }) =>
    () => {
      const reqId = uniqueId('request')
      const variables = getVariables({
        after: loadAll ? null : state.after,
        query: state.query,
        first: loadAll ? state.first : PAGE_SIZE,
      })

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      api.projection
        .list(variables, state.tab)
        .then(({ edges }) => {
          const items: IProjectionImport[] = edges.map((e) => e.node)

          dispatch({
            type: Actions.LOAD,
            count: items.length,
            projections: items,
            pageInfo: {} as IPageInfo,
            reqId,
          })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }

  //api request without skeleton and with load only 25 items after cursor
  useEffect(fetchProjections({ showSkeleton: false, loadAll: false }), [state.after, state.tab])

  //api request without skeleton and with load all items in state.first
  useEffect(fetchProjections({ showSkeleton: false, loadAll: true }), [shouldReload.projections])

  //api request with skeleton and with load all items in state.first
  useEffect(fetchProjections({ showSkeleton: true, loadAll: true }), [state.query])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  return {
    ...state,
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
    },
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, column })
    },
    applyQuery: (query: string) => {
      dispatch({ type: Actions.QUERY, query })
    },
    changeTab: (tab: string) => {
      dispatch({ type: Actions.CHANGE_TAB, tab })
    },
    clearQuery: () => {
      dispatch({
        type: Actions.QUERY,
        query: '',
      })
    },
    reload: () => reload.projections(),
    selection: selection,
    selectDetail: (detail: IProjectionImport | null) => {
      dispatch({ type: Actions.DETAIL_VIEW, detail })
    },
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
        isLoading: true,
      }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        reqId: action.reqId,
        isLoading: false,
      }

    case Actions.ERROR:
      return action.reqId === state.reqId
        ? { ...state, isLoading: false, isSorting: false, isPaging: false }
        : state

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            isLoading: false,
            isSorting: false,
            isPaging: false,
            count: action.count,
            items: state.isPaging ? [...state.items, ...action.projections] : action.projections,
            pageInfo: action.pageInfo,
          }
        : state

    case Actions.SORT:
      return {
        ...state,
        isSorting: true,
        pageInfo: { hasNextPage: false, endCursor: '' },
        sort: {
          column: action.column,
          order:
            state.sort.column === action.column
              ? state.sort.order === Order.ASC
                ? Order.DESC
                : Order.ASC
              : Order.ASC,
        },
      }

    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        isPaging: true,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
      }

    case Actions.QUERY:
      return {
        ...state,
        query: action.query,
        pageInfo: { hasNextPage: false, endCursor: '' },
      }
    case Actions.CHANGE_TAB:
      return {
        ...state,
        tab: action.tab,
      }
    case Actions.DETAIL_VIEW:
      return {
        ...state,
        detail: action.detail,
      }

    default:
      return state
  }
}
